var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Blockchain, useSessionContext } from '@dfx.swiss/react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useIframe } from '../hooks/iframe.hook';
import { useStore } from '../hooks/store.hook';
import { url } from '../util/utils';
import { useBalanceContext } from './balance.context';
// --- INTERFACES --- //
// CAUTION: params need to be added to index-widget.tsx
const urlParamsToRemove = [
    'lang',
    'address',
    'signature',
    'mail',
    'wallet',
    'refcode',
    'special-code',
    'session',
    'redirect',
    'type',
    'redirect-uri',
    'balances',
];
export var CloseType;
(function (CloseType) {
    CloseType["BUY"] = "buy";
    CloseType["SELL"] = "sell";
    CloseType["SWAP"] = "swap";
    CloseType["CANCEL"] = "cancel";
})(CloseType || (CloseType = {}));
const AppHandlingContext = createContext(undefined);
export function useAppHandlingContext() {
    return useContext(AppHandlingContext);
}
export function AppHandlingContextProvider(props) {
    const { redirectUri: storeRedirectUri } = useStore();
    const { isUsedByIframe, sendMessage } = useIframe();
    const { readBalances } = useBalanceContext();
    const { availableBlockchains } = useSessionContext();
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasSession, setHasSession] = useState(false);
    const [redirectUri, setRedirectUri] = useState();
    const [params, setParams] = useState({});
    const [redirectPath, setRedirectPath] = useState();
    const search = window.location.search;
    const query = new URLSearchParams(search);
    useEffect(() => {
        init();
    }, []);
    useEffect(() => {
        if (!redirectUri)
            setRedirectUri(storeRedirectUri.get());
    }, []);
    // parameters
    function getParameter(query, key) {
        var _a;
        return (_a = query.get(key)) !== null && _a !== void 0 ? _a : undefined;
    }
    function setParameters(params) {
        setParams((p) => (Object.assign(Object.assign({}, p), params)));
    }
    function init() {
        return __awaiter(this, void 0, void 0, function* () {
            const params = extractUrlParams(props.params);
            setParams(params);
            if (params.redirectUri) {
                setRedirectUri(params.redirectUri);
                storeRedirectUri.set(params.redirectUri);
            }
            const hasSession = Boolean(params.session || (params.address && params.signature));
            setHasSession(hasSession);
            if (params.balances || hasSession) {
                readBalances(params.balances);
            }
            setIsInitialized(true);
            removeUrlParams(query);
        });
    }
    function extractUrlParams(params) {
        return params
            ? Object.assign({ session: getParameter(query, 'session'), redirect: getParameter(query, 'redirect'), type: getParameter(query, 'type') }, params) : {
            headless: getParameter(query, 'headless'),
            borderless: getParameter(query, 'borderless'),
            flags: getParameter(query, 'flags'),
            lang: getParameter(query, 'lang'),
            address: getParameter(query, 'address'),
            signature: getParameter(query, 'signature'),
            mail: getParameter(query, 'mail'),
            wallet: getParameter(query, 'wallet'),
            wallets: getParameter(query, 'wallets'),
            refcode: getParameter(query, 'refcode'),
            specialCode: getParameter(query, 'special-code'),
            session: getParameter(query, 'session'),
            redirect: getParameter(query, 'redirect'),
            type: getParameter(query, 'type'),
            redirectUri: getParameter(query, 'redirect-uri'),
            mode: getParameter(query, 'mode'),
            blockchain: Object.values(Blockchain).find((b) => { var _a; return b.toLowerCase() === ((_a = getParameter(query, 'blockchain')) === null || _a === void 0 ? void 0 : _a.toLowerCase()); }),
            blockchains: getParameter(query, 'blockchains'),
            balances: getParameter(query, 'balances'),
            amountIn: getParameter(query, 'amount-in'),
            amountOut: getParameter(query, 'amount-out'),
            assets: getParameter(query, 'assets'),
            assetIn: getParameter(query, 'asset-in'),
            assetOut: getParameter(query, 'asset-out'),
            paymentMethod: getParameter(query, 'payment-method'),
            bankAccount: getParameter(query, 'bank-account'),
            externalTransactionId: getParameter(query, 'external-transaction-id'),
        };
    }
    function removeUrlParams(query) {
        if (urlParamsToRemove.map((param) => query.has(param)).every((b) => !b))
            return;
        urlParamsToRemove.forEach((param) => query.delete(param));
        const path = props.router.state.location.pathname;
        props.router.navigate(url(path, query), { replace: true });
        const { location, history } = window;
        history.replaceState(undefined, '', url(`${location.origin}${location.pathname}`, query));
    }
    // closing
    function closeServices(params, navigate) {
        var _a;
        if (props.isWidget) {
            (_a = props.closeCallback) === null || _a === void 0 ? void 0 : _a.call(props, createCloseMessageData(params));
        }
        else {
            if (isUsedByIframe) {
                sendMessage(createCloseMessageData(params));
            }
            if (redirectUri) {
                const uri = getRedirectUri(redirectUri, params);
                storeRedirectUri.remove();
                window.location = uri;
            }
        }
        if (navigate)
            props.router.navigate('/');
    }
    function getRedirectUri(baseUri, params) {
        let uri = new URL(baseUri);
        switch (params.type) {
            case CloseType.BUY:
                uri = adaptUri(uri, params.type);
                break;
            case CloseType.SELL:
                uri = adaptUri(uri, params.type, {
                    routeId: params.sell.routeId.toString(),
                    amount: params.sell.amount.toString(),
                    asset: params.sell.asset.name,
                    blockchain: params.sell.asset.blockchain,
                    isComplete: params.isComplete.toString(),
                });
                break;
            case CloseType.SWAP:
                uri = adaptUri(uri, params.type, {
                    routeId: params.swap.routeId.toString(),
                    amount: params.swap.amount.toString(),
                    asset: params.swap.sourceAsset.name,
                    blockchain: params.swap.sourceAsset.blockchain,
                    isComplete: params.isComplete.toString(),
                });
                break;
        }
        return uri.toString();
    }
    function adaptUri(uri, path, params) {
        params && Object.entries(params).forEach(([key, val]) => uri.searchParams.set(key, val));
        if (uri.origin === 'null') {
            // custom solution for deep link URIs
            const pathname = uri.pathname ? uri.pathname : '//';
            const newUrl = adaptPath(uri.protocol + pathname, path);
            return new URL(url(newUrl, uri.searchParams));
        }
        else {
            uri.pathname = adaptPath(uri.pathname, path);
            return uri;
        }
    }
    function adaptPath(path, newElement) {
        return path + (path.endsWith('/') ? newElement : `/${newElement}`);
    }
    function createCloseMessageData(params) {
        switch (params.type) {
            case CloseType.BUY:
            case CloseType.SELL:
            case CloseType.SWAP:
                return params;
            default:
                return { type: CloseType.CANCEL };
        }
    }
    const context = useMemo(() => {
        var _a;
        return ({
            isEmbedded: props.isWidget || isUsedByIframe,
            hasSession,
            isDfxHosted: ((_a = window.location.hostname) === null || _a === void 0 ? void 0 : _a.split('.').slice(-2).join('.')) === 'dfx.swiss',
            closeServices,
            isInitialized,
            params,
            setParams: setParameters,
            availableBlockchains: availableBlockchains === null || availableBlockchains === void 0 ? void 0 : availableBlockchains.filter((b) => !params.blockchains ||
                params.blockchains
                    .split(',')
                    .map((b1) => b1.toLowerCase())
                    .includes(b.toLowerCase())),
            redirectPath,
            setRedirectPath,
            canClose: redirectUri != null,
            service: props.service,
        });
    }, [
        props.isWidget,
        props.service,
        isUsedByIframe,
        redirectUri,
        isInitialized,
        params,
        redirectPath,
        availableBlockchains,
    ]);
    return _jsx(AppHandlingContext.Provider, Object.assign({ value: context }, { children: props.children }));
}
